import GUI from 'lil-gui'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

import portalVertex from './shaders/portal/vertex.glsl'
import portalFragment from './shaders/portal/fragment.glsl'

import firefliesVertex from './shaders/firefly/vertex.glsl'
import firefliesFragment from './shaders/firefly/fragment.glsl'

/**
 * Base
 */
// Debug
const gui = new GUI({
})

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Loaders
 */
// Texture loader
const textureLoader = new THREE.TextureLoader()

const baked = textureLoader.load('./portal_baked.png')
const perlinTexture = textureLoader.load('./perlin.png')
perlinTexture.wrapS = true
perlinTexture.wrapT = true

const bakedMaterial = new THREE.MeshBasicMaterial({
    map: baked,
})
baked.flipY = false
baked.colorSpace = THREE.SRGBColorSpace

const portalUniforms = {
    uTime: {value: 0},
    uPerlinTexture: {value: perlinTexture},
    uPortalOpening: {value: 4.31},
    uPortalColor: { value: new THREE.Color(0x3868FF)},
    uRipples: {value: 16},
    uRippleHeight: {value: 0.13},
    uRippleSpeed: {value: 2.8},
    uPerlinBlend: {value: 1},
    uPerlinMultiply: {value: 1}
}

const lanternEmissiveMaterial = new THREE.MeshBasicMaterial({color: 0xffffe5})
const portalEmissiveMaterial = new THREE.ShaderMaterial({
    fragmentShader: portalFragment,
    vertexShader: portalVertex,
    uniforms: portalUniforms,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: true,
    // blending: THREE.AdditiveBlending,
})
const firefliesUniforms = {
    uTime: {value: 0},
    uPixelRatio: {value: Math.min(window.devicePixelRatio, 2)}
}

const firefliesMaterial = new THREE.ShaderMaterial({
    fragmentShader: firefliesFragment,
    vertexShader: firefliesVertex,
    uniforms: firefliesUniforms,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})

// GLTF loader
const gltfLoader = new GLTFLoader()

gltfLoader.loadAsync('./portal2.glb').then(gltf => {
    gltf.scene.traverse(child => {
        if(child.type = "Mesh") {
            child.material = bakedMaterial
        }
    })
    scene.add(gltf.scene)

    const portalLightMesh = gltf.scene.children.find(child => child.name == 'portalLight')
    const lanternLightA = gltf.scene.children.find(child => child.name == 'lanternLightA')
    const lanternLightB = gltf.scene.children.find(child => child.name == 'lanternLightB')

    portalLightMesh.material = portalEmissiveMaterial
    lanternLightA.material = lanternEmissiveMaterial
    lanternLightB.material = lanternEmissiveMaterial
})

/**
 * Object
 */
// const cube = new THREE.Mesh(
//     new THREE.BoxGeometry(1, 1, 1),
//     new THREE.MeshBasicMaterial()
// )

// scene.add(cube)
// const ambientLight = new THREE.AmbientLight(new THREE.Color("white"), 100)
// scene.add(ambientLight)

/**
 * Fireflies
 */
const firefliesGeometry = new THREE.BufferGeometry()
const firefliesCount = 30
const positionArray = new Float32Array(firefliesCount * 3)
const scaleArray = new Float32Array(firefliesCount)
for(let i = 0; i < firefliesCount; i++) {
    positionArray[i * 3 + 0] = 4 * (Math.random() - 0.5)
    positionArray[i * 3 + 1] = 1.5 * Math.random()
    positionArray[i * 3 + 2] = 4 * (Math.random() - 0.5)

    scaleArray[i] = 0.8 * Math.random() + 0.2
}

firefliesGeometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3))
firefliesGeometry.setAttribute('aScale', new THREE.BufferAttribute(scaleArray, 1))
const fireflies = new THREE.Points(firefliesGeometry, firefliesMaterial)

scene.add(fireflies)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

gui.add(portalUniforms.uPortalOpening, 'value').min(Math.PI).max(2 * Math.PI).step(0.01).name("opening")
gui.add(portalUniforms.uRipples, 'value').min(1).max(128).step(1).name("ripples")
gui.add(portalUniforms.uRippleHeight, 'value').min(0).max(1).step(0.01).name("ripple height")
gui.add(portalUniforms.uRippleSpeed, 'value').min(0).max(20).step(0.1).name("ripple speed")
gui.add(portalUniforms.uPerlinBlend, 'value').min(0).max(1).step(0.1).name("perlin blend")
gui.add(portalUniforms.uPerlinMultiply, 'value').min(0).max(1).step(0.1).name("perlin multiply")
gui.close()

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    firefliesUniforms.uPixelRatio.value = Math.min(window.devicePixelRatio, 2)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100)
camera.position.x = -3.5
camera.position.y = 2.5
camera.position.z = -3.5
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.maxPolarAngle = Math.PI / 2
controls.minDistance = 1
controls.maxDistance = 10

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor(new THREE.Color(0x040814))

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    portalUniforms.uTime.value = elapsedTime
    firefliesUniforms.uTime.value = elapsedTime

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()